import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";

const PROXY_URL = process.env.REACT_APP_PROXY_URL || "http://localhost:3001";
const FREE_LIMIT = 5;

export default function AccountPage() {
  const navigate   = useNavigate();
  const { user, isLoaded } = useUser();
  const { signOut, openUserProfile } = useClerk();

  const [isPro, setIsPro]           = useState(false);
  const [plan, setPlan]             = useState(null);
  const [remaining, setRemaining]   = useState(null);
  const [limit, setLimit]           = useState(FREE_LIMIT);
  const [loading, setLoading]       = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [theme, setTheme]           = useState(() => localStorage.getItem("limji_theme") || "dark");

  const th = theme === "dark" ? {
    bg: "#0c0a09", surface: "#1a1714", card: "#211e1b", border: "#2e2a26",
    text: "#f5f0eb", textDim: "#a39585", textMuted: "#6b5f52",
    accent: "#d97706", accentBg: "rgba(217,119,6,0.1)",
  } : {
    bg: "#faf8f5", surface: "#f0ece6", card: "#e8e3dc", border: "#d4cdc4",
    text: "#1a1714", textDim: "#5c5248", textMuted: "#9c8f84",
    accent: "#c96a00", accentBg: "rgba(201,106,0,0.08)",
  };

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) { navigate("/sign-in"); return; }

    const params = new URLSearchParams(window.location.search);
    const justUpgraded = params.get("upgraded") === "true";
    if (justUpgraded) window.history.replaceState({}, "", "/account");

    // If just upgraded, wait 2s for webhook to fire then re-fetch
    const delay = justUpgraded ? 2000 : 0;

    setTimeout(() => {
      // Fetch subscription + usage in parallel
      Promise.all([
        fetch(`${PROXY_URL}/api/ls/subscription/${user.id}`).then(r => r.json()),
        fetch(`${PROXY_URL}/api/usage`, { headers: { "x-clerk-user-id": user.id } }).then(r => r.json()),
      ]).then(([sub, usage]) => {
        setIsPro(sub.subscribed || false);
        setPlan(sub.plan || null);
        setRemaining(usage.remaining ?? FREE_LIMIT);
        setLimit(usage.limit ?? FREE_LIMIT);
      }).catch(() => {
        setRemaining(FREE_LIMIT);
        setLimit(FREE_LIMIT);
      }).finally(() => setLoading(false));
    }, delay);
  }, [isLoaded, user, navigate]);

  async function startCheckout(plan) {
    if (!user) { navigate("/sign-up"); return; }
    setCheckoutLoading(true);
    try {
      const res  = await fetch(`${PROXY_URL}/api/ls/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clerkUserId: user.id, userEmail: user.primaryEmailAddress?.emailAddress, plan }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert("Something went wrong. Please try again.");
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  }

  async function openBillingPortal() {
    setPortalLoading(true);
    try {
      const res  = await fetch(`${PROXY_URL}/api/ls/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clerkUserId: user.id, userEmail: user.primaryEmailAddress?.emailAddress }),
      });
      const data = await res.json();
      if (data.url) window.open(data.url, "_blank");
      else alert("Could not open billing portal. Please try again.");
    } catch {
      alert("Could not open billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  }

  const used       = limit - (remaining ?? limit);
  const usedPct    = Math.min(100, Math.round((used / limit) * 100));
  const memberSince = user?.createdAt ? new Date(user.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : null;

  if (!isLoaded || loading) return (
    <div style={{ minHeight: "100vh", background: th.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 32, height: 32, border: `3px solid ${th.border}`, borderTopColor: th.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Instrument+Sans:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${th.bg}; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .ac-card { background: ${th.surface}; border: 1px solid ${th.border}; border-radius: 16px; padding: 24px; animation: fadeUp 0.4s ease both; }
        .ac-card:hover { border-color: ${th.accent}22; }
        .ac-btn { border: none; border-radius: 10px; padding: 11px 20px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; transition: all 0.15s; display: inline-flex; align-items: center; gap: 8px; }
        .ac-btn:hover { opacity: 0.85; transform: translateY(-1px); }
        .ac-btn-primary { background: ${th.accent}; color: #0c0a09; }
        .ac-btn-ghost { background: transparent; color: ${th.textDim}; border: 1px solid ${th.border} !important; }
        .ac-btn-ghost:hover { border-color: ${th.accent} !important; color: ${th.accent}; opacity: 1; transform: none; }
        .ac-btn-danger { background: transparent; color: #ef4444; border: 1px solid #ef444433 !important; }
        .ac-btn-danger:hover { background: #ef444411; opacity: 1; transform: none; }
        .ac-label { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: ${th.textMuted}; margin-bottom: 6px; }
        .ac-value { font-size: 15px; font-weight: 600; color: ${th.text}; }
      `}</style>

      <div style={{ minHeight: "100vh", background: th.bg, fontFamily: "'Instrument Sans', sans-serif", color: th.text }}>

        {/* Header */}
        <div style={{ borderBottom: `1px solid ${th.border}`, background: th.surface, position: "sticky", top: 0, zIndex: 10 }}>
          <div style={{ maxWidth: 760, margin: "0 auto", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer" }}>
              <div style={{ width: 32, height: 32, background: th.accent, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, color: "#0c0a09", fontWeight: 800 }}>✦</div>
              <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: th.text, letterSpacing: "-0.01em" }}>Limji</span>
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button className="ac-btn ac-btn-ghost" onClick={() => navigate("/app")}>← Back to app</button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 24px 80px" }}>

          {/* Page title */}
          <div style={{ marginBottom: 32, animation: "fadeUp 0.4s ease both" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
              {user?.imageUrl ? (
                <img src={user.imageUrl} alt="" style={{ width: 52, height: 52, borderRadius: "50%", border: `2px solid ${th.border}` }} />
              ) : (
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: th.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#0c0a09", fontWeight: 800 }}>
                  {user?.firstName?.[0] || user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div>
                <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 800, color: th.text, letterSpacing: "-0.01em" }}>
                  {user?.firstName ? `${user.firstName}${user.lastName ? " " + user.lastName : ""}` : "Your Account"}
                </h1>
                <p style={{ fontSize: 13, color: th.textDim, marginTop: 2 }}>{user?.primaryEmailAddress?.emailAddress}</p>
              </div>
            </div>
            {memberSince && <p style={{ fontSize: 12, color: th.textMuted, marginTop: 8 }}>Member since {memberSince}</p>}
          </div>

          <div style={{ display: "grid", gap: 16 }}>

            {/* Plan card */}
            <div className="ac-card" style={{ animationDelay: "0.05s", borderColor: isPro ? `${th.accent}44` : th.border, background: isPro ? `linear-gradient(135deg, ${th.surface} 0%, ${th.accentBg} 100%)` : th.surface }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                <div>
                  <div className="ac-label">Current Plan</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
                    <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 800, color: th.text }}>
                      {isPro ? "Pro" : "Free"}
                    </span>
                    {isPro && (
                      <span style={{ background: th.accent, color: "#0c0a09", fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 20, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        {plan === "yearly" ? "Annual" : "Monthly"}
                      </span>
                    )}
                    {!isPro && (
                      <span style={{ background: th.card, border: `1px solid ${th.border}`, color: th.textMuted, fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        Free tier
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 13, color: th.textDim, marginTop: 6 }}>
                    {isPro
                      ? plan === "yearly" ? "$149/year — best value" : "$19/month"
                      : "3 generations per day · resets at midnight"}
                  </p>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {isPro ? (
                    <button className="ac-btn ac-btn-ghost" onClick={openBillingPortal} disabled={portalLoading}>
                      {portalLoading ? "Loading..." : "⚙ Manage billing"}
                    </button>
                  ) : (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="ac-btn ac-btn-primary" onClick={() => startCheckout("monthly")} disabled={checkoutLoading}>
                        {checkoutLoading ? "Loading..." : "✦ Go Pro — $19/mo"}
                      </button>
                      <button className="ac-btn ac-btn-ghost" onClick={() => startCheckout("yearly")} disabled={checkoutLoading}>
                        {checkoutLoading ? "..." : "Annual — $149/yr"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isPro && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${th.border}`, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 }}>
                  {[
                    { label: "Generations/day", value: "150" },
                    { label: "Smart Ads", value: "All 10 categories" },
                    { label: "Smart Email", value: "All 21 types" },
                    { label: "Support", value: "Priority" },
                  ].map(item => (
                    <div key={item.label}>
                      <div className="ac-label">{item.label}</div>
                      <div className="ac-value">{item.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Usage card */}
            <div className="ac-card" style={{ animationDelay: "0.1s" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div className="ac-label">Today's Usage</div>
                  <div style={{ marginTop: 4, display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, color: th.text }}>{used}</span>
                    <span style={{ fontSize: 15, color: th.textMuted }}>/ {limit}</span>
                    <span style={{ fontSize: 13, color: th.textDim, marginLeft: 4 }}>generations used</span>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="ac-label">Remaining</div>
                  <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, color: remaining === 0 ? "#ef4444" : th.accent, marginTop: 4 }}>
                    {remaining ?? "—"}
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ background: th.card, borderRadius: 8, height: 8, overflow: "hidden", border: `1px solid ${th.border}` }}>
                <div style={{
                  height: "100%",
                  width: `${usedPct}%`,
                  background: remaining === 0 ? "#ef4444" : usedPct > 80 ? "#f59e0b" : th.accent,
                  borderRadius: 8,
                  transition: "width 0.6s ease",
                }} />
              </div>
              <p style={{ fontSize: 12, color: th.textMuted, marginTop: 8 }}>
                Resets at midnight · {isPro ? "150 generations/day on Pro" : "3 generations/day on Free"}
              </p>

              {!isPro && remaining === 0 && (
                <div style={{ marginTop: 14, padding: "12px 16px", background: th.accentBg, border: `1px solid ${th.accent}44`, borderRadius: 10 }}>
                  <p style={{ fontSize: 13, color: th.text, fontWeight: 600 }}>You've used all your free generations today.</p>
                  <p style={{ fontSize: 12, color: th.textDim, marginTop: 4 }}>Upgrade to Pro for 150/day — or come back tomorrow.</p>
                  <button className="ac-btn ac-btn-primary" style={{ marginTop: 12 }} onClick={() => startCheckout("monthly")} disabled={checkoutLoading}>
                    {checkoutLoading ? "Loading..." : "✦ Go Pro — $19/mo"}
                  </button>
                </div>
              )}
            </div>

            {/* What's included */}
            <div className="ac-card" style={{ animationDelay: "0.15s" }}>
              <div className="ac-label" style={{ marginBottom: 16 }}>What's included</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                {[
                  { icon: "📢", label: "Smart Ads", desc: "10 categories" },
                  { icon: "✉️", label: "Smart Email", desc: "21 types" },
                  { icon: "📋", label: "Templates", desc: "Save & reuse" },
                  { icon: "🕐", label: "History", desc: "All generations" },
                  { icon: "⬇", label: "Export", desc: "PDF & TXT" },
                  { icon: "🌙", label: "Dark mode", desc: "Your preference" },
                ].map(f => (
                  <div key={f.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: th.card, borderRadius: 10, border: `1px solid ${th.border}` }}>
                    <span style={{ fontSize: 18 }}>{f.icon}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: th.text }}>{f.label}</div>
                      <div style={{ fontSize: 11, color: th.textMuted }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Account settings */}
            <div className="ac-card" style={{ animationDelay: "0.2s" }}>
              <div className="ac-label" style={{ marginBottom: 16 }}>Account Settings</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: th.card, borderRadius: 10, border: `1px solid ${th.border}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: th.text }}>Email address</div>
                    <div style={{ fontSize: 12, color: th.textDim, marginTop: 2 }}>{user?.primaryEmailAddress?.emailAddress}</div>
                  </div>
                  <button className="ac-btn ac-btn-ghost" style={{ fontSize: 12, padding: "7px 14px" }} onClick={() => openUserProfile()}>Edit</button>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: th.card, borderRadius: 10, border: `1px solid ${th.border}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: th.text }}>Password & security</div>
                    <div style={{ fontSize: 12, color: th.textDim, marginTop: 2 }}>Change password, active devices</div>
                  </div>
                  <button className="ac-btn ac-btn-ghost" style={{ fontSize: 12, padding: "7px 14px" }} onClick={() => openUserProfile()}>Manage</button>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: th.card, borderRadius: 10, border: `1px solid ${th.border}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: th.text }}>Theme</div>
                    <div style={{ fontSize: 12, color: th.textDim, marginTop: 2 }}>Currently {theme === "dark" ? "dark" : "light"} mode</div>
                  </div>
                  <button className="ac-btn ac-btn-ghost" style={{ fontSize: 12, padding: "7px 14px" }} onClick={() => {
                    const next = theme === "dark" ? "light" : "dark";
                    setTheme(next);
                    localStorage.setItem("limji_theme", next);
                  }}>
                    {theme === "dark" ? "☀ Light" : "🌙 Dark"}
                  </button>
                </div>
              </div>
            </div>

            {/* Danger zone */}
            <div className="ac-card" style={{ animationDelay: "0.25s", borderColor: "#ef444422" }}>
              <div className="ac-label" style={{ marginBottom: 16, color: "#ef4444" }}>Danger Zone</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: th.text }}>Sign out</div>
                  <div style={{ fontSize: 12, color: th.textDim, marginTop: 2 }}>You'll need to sign in again to use your account</div>
                </div>
                <button className="ac-btn ac-btn-danger" onClick={() => signOut(() => navigate("/"))}>Sign out</button>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div style={{ marginTop: 48, textAlign: "center" }}>
            <p style={{ fontSize: 12, color: th.textMuted }}>
              Need help? <a href="mailto:support@limji.app" style={{ color: th.accent, textDecoration: "none" }}>support@limji.app</a>
            </p>
          </div>

        </div>
      </div>
    </>
  );
}
